import { gql } from '@apollo/client';
import IconCheck from '@apollo/icons/default/IconCheck.svg';
import IconLinter from '@apollo/icons/default/IconLinter.svg';
import IconOperations from '@apollo/icons/default/IconOperations.svg';
import IconProposals from '@apollo/icons/default/IconProposals.svg';
import IconSatellite3 from '@apollo/icons/default/IconSatellite3.svg';
import IconSchema from '@apollo/icons/default/IconSchema.svg';
import { Group, List, ListIcon, ListItem } from '@apollo/orbit';
import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { useRouteParams } from 'src/hooks/useRouteParams';
import { assertUnreachable } from 'src/lib/assertUnreachable';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import {
  TaskStatusIcon,
  TaskStatusIconTaskFragment,
} from '../../checksPage/taskStatusIcon/TaskStatusIcon';
import { useInternalGraphLinking } from '../../hooks/useInternalGraphLinking';
import { CheckTaskType } from '../../routes';

import { taskTypeDisplayValue } from './CheckTasks';
import { taskToTaskType, taskToTaskTypeTaskFragment } from './taskToTaskType';

export const TasksMenuTaskFragment = gql`
  fragment TasksMenuTaskFragment on CheckWorkflowTask {
    id
    status
    ...taskToTaskTypeTaskFragment
    ...TaskStatusIconTaskFragment
  }
  ${taskToTaskTypeTaskFragment}
  ${TaskStatusIconTaskFragment}
`;

function taskTypeIcon(taskType: CheckTaskType) {
  return taskType === 'build' || taskType === 'filter'
    ? IconSchema
    : taskType === 'operations'
      ? IconOperations
      : taskType === 'linter'
        ? IconLinter
        : taskType === 'downstream'
          ? IconSatellite3
          : taskType === 'proposals'
            ? IconProposals
            : taskType === 'custom'
              ? IconCheck
              : assertUnreachable(taskType);
}

export function TasksMenu({
  defaultTaskTab,
  tasks,
}: {
  defaultTaskTab: CheckTaskType | 'unknown';
  tasks: GraphQLTypes.TasksMenuTaskFragment[];
}) {
  const location = useLocation();
  const { currentCheckTasksRoute } = useInternalGraphLinking();
  const { taskType } = useRouteParams(currentCheckTasksRoute);

  return (
    <List isInteractive={true}>
      {tasks.map((task) => {
        const isDisabled =
          task.status === GraphQLTypes.CheckWorkflowTaskStatus.BLOCKED;
        const slug = taskToTaskType(task);
        const active =
          taskType === slug ||
          (taskType === undefined && slug === defaultTaskTab);
        if (slug === 'unknown') {
          return null;
        }
        return (
          <li key={slug}>
            {/* ListItem current doesn't work as intended. We have to use the `as` prop to make it the link, and wrap it with `<li/>`. Once ListItem is updated, we will hopefully be able to nest the link inside the listItem instead  */}
            <ListItem
              isSelected={active}
              isDisabled={isDisabled}
              as={isDisabled ? undefined : NavLink}
              to={currentCheckTasksRoute.locationFrom({
                location,
                patch: { taskType: slug },
              })}
              isActive={() => active}
              className="flex w-full items-center justify-between"
            >
              <Group orientation="horizontal">
                <ListIcon as={taskTypeIcon(slug)} />
                {taskTypeDisplayValue(slug)}
              </Group>
              <TaskStatusIcon task={task} />
            </ListItem>
          </li>
        );
      })}
    </List>
  );
}
